// src/auth/screens/AuthCard.jsx
//
// Shared chrome for the three PUBLIC recovery screens (/forgot-password,
// /password-reset, /set-password) so they are visually identical to /login.
//
// ⚠️ LoginScreen.jsx deliberately keeps its own copy of this markup: the repo
// rule is "extract only, never alter logic/prompts/rendering in the same
// commit", and restyling the login screen inside a feature commit would break
// it. The brand SVG is duplicated once; that is the accepted cost.
import React from 'react';
import { COLORS, SEMANTIC, RADII, SHADOW, FONT_SANS, FONT_MONO } from '../../constants/theme.js';

export const authLabelStyle = {
  display: 'block',
  fontSize: '10px',
  fontFamily: FONT_MONO,
  color: COLORS.textSecondary,
  letterSpacing: '1.2px',
  textTransform: 'uppercase',
  marginBottom: '8px',
};

/** Inline error/notice block, same visual language as LoginScreen's error box. */
export function AuthNotice({ tone = 'error', children }) {
  const pair = tone === 'error' ? SEMANTIC.errore
    : tone === 'success' ? SEMANTIC.verde
    : tone === 'warning' ? SEMANTIC.attenzione   // NEW (v221): «Termini aggiornati» on /set-password
    : SEMANTIC.info;
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      style={{
        marginBottom: '18px',
        padding: '10px 14px',
        borderRadius: RADII.control,
        background: pair.bg,
        border: `1px solid ${pair.fg}`,
        color: pair.fg,
        fontSize: '13px',
      }}
    >
      {children}
    </div>
  );
}

export default function AuthCard({ title, intro, children }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: COLORS.bg,
        fontFamily: FONT_SANS,
        padding: '24px',
        boxSizing: 'border-box',
      }}
    >
      <style>{`
        .auth-input {
          width: 100%;
          padding: 12px 16px;
          border-radius: ${RADII.control};
          border: 1px solid ${COLORS.border};
          background: ${COLORS.field};
          color: ${COLORS.ink};
          font-size: 14px;
          font-family: ${FONT_SANS};
          box-sizing: border-box;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .auth-input::placeholder { color: ${COLORS.textTertiary}; }
        .auth-input:focus {
          border-color: ${COLORS.accent};
          box-shadow: 0 0 0 3px ${COLORS.accentSoft};
        }
        .auth-submit {
          width: 100%;
          padding: 13px;
          border-radius: ${RADII.control};
          border: none;
          background: ${COLORS.accent};
          color: ${COLORS.surface};
          font-size: 15px;
          font-weight: 700;
          font-family: ${FONT_SANS};
          cursor: pointer;
          transition: filter 0.15s;
        }
        .auth-submit:hover:not(:disabled) { filter: brightness(1.08); }
        .auth-submit:disabled { background: ${COLORS.borderStrong}; cursor: not-allowed; }
        .auth-link {
          background: none;
          border: none;
          padding: 0;
          font-family: ${FONT_SANS};
          font-size: 13px;
          color: ${COLORS.accent};
          cursor: pointer;
          text-decoration: none;
        }
        .auth-link:hover { text-decoration: underline; }
      `}</style>

      <div
        style={{
          width: '100%',
          maxWidth: '400px',
          background: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: RADII.section,
          boxShadow: SHADOW.card,
          padding: '40px',
          boxSizing: 'border-box',
        }}
      >
        {/* Brand lockup — same glyph + wordmark as LoginScreen / Sidebar. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '28px' }}>
          <svg width="40" height="40" viewBox="0 0 44 44" fill={COLORS.accent} xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
            <path d="M38.2966 8.77426L32.5513 14.5408C30.7496 16.3492 28.1694 16.8634 25.8155 15.8849C23.4616 14.9065 22 12.711 22 10.1541V1.99927H26.4146V10.1541C26.4146 11.2526 27.2486 11.6854 27.5048 11.7911C27.7609 11.8968 28.6547 12.1839 29.4289 11.4068L35.1742 5.64031L38.2951 8.77283L38.2966 8.77426Z" />
            <path d="M14.6153 21.9971H6.49048V17.5662H14.6153C15.7097 17.5662 16.1409 16.7291 16.2462 16.472C16.3515 16.2149 16.6376 15.3178 15.8634 14.5408L10.1181 8.77425L13.2391 5.64172L18.9844 11.4082C20.7861 13.2166 21.2984 15.8063 20.3236 18.169C19.3487 20.5316 17.1613 21.9985 14.6139 21.9985L14.6153 21.9971Z" />
            <path d="M26.4146 37.564H22V29.4092C22 28.3108 21.166 27.8779 20.9098 27.7722C20.6537 27.6665 19.7599 27.3794 18.9857 28.1565L5.19252 42.0007L2.07581 38.8625L15.8633 25.024C17.6651 23.2156 20.2452 22.6999 22.5991 23.6798C24.953 24.6583 26.4146 26.8538 26.4146 29.4106V37.5655V37.564Z" />
            <path d="M35.1741 33.923L29.4288 28.1565C27.6271 26.3481 27.1148 23.7584 28.0896 21.3958C29.0645 19.0331 31.2519 17.5662 33.7993 17.5662H41.9241V21.9971H33.7993C32.7049 21.9971 32.2737 22.8342 32.1684 23.0913C32.0631 23.3484 31.777 24.2454 32.5512 25.0225L38.2965 30.789L35.1755 33.9216L35.1741 33.923Z" />
          </svg>
          <span style={{ fontSize: '24px', fontWeight: 700, color: COLORS.ink, fontFamily: FONT_SANS, letterSpacing: '-0.2px' }}>brian</span>
        </div>

        <h1 style={{ color: COLORS.ink, fontSize: '24px', fontWeight: 700, margin: '0 0 12px' }}>
          {title}
        </h1>
        {intro && (
          <p style={{ color: COLORS.textSecondary, fontSize: '13px', lineHeight: 1.5, margin: '0 0 24px' }}>
            {intro}
          </p>
        )}

        {children}
      </div>
    </div>
  );
}
